import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { 
  Package, Plus, Trash2, Edit, DollarSign, RefreshCw, CheckCircle, XCircle, 
  Calendar, TrendingUp, Users, UserPlus, Clock, AlertCircle, Search,
  Loader2, BarChart3, Gift, Percent, Eye, Play, History, ChevronRight,
  Settings, Award, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TreatmentPackages = () => {
  const [packages, setPackages] = useState([]);
  const [stats, setStats] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [assignedPackages, setAssignedPackages] = useState([]);
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);  // Membership plans for tier discounts
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packages');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDiscountSettingsModal, setShowDiscountSettingsModal] = useState(false);
  
  const [editingPackage, setEditingPackage] = useState(null);
  const [selectedPackageForAssign, setSelectedPackageForAssign] = useState(null);
  const [selectedMemberPackage, setSelectedMemberPackage] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [pricePreview, setPricePreview] = useState(null);  // Member discount preview
  const [priceLoading, setPriceLoading] = useState(false);
  
  // Member discount settings
  const [discountSettings, setDiscountSettings] = useState({
    enabled: false,
    global_discount_percent: 0,
    stack_with_package_discount: true,
    tier_discounts: []
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',  // For savings calculation
    discount_percentage: '',
    duration_days: '',
    items: [{ service_name: '', quantity: 1, unit_price: '', description: '' }],
    is_active: true
  });
  
  const [assignForm, setAssignForm] = useState({
    member_id: '',
    payment_method: 'cash',
    payment_notes: '',
    reference_number: '',
    start_date: ''
  });
  
  const [usageForm, setUsageForm] = useState({
    item_index: 0,
    quantity_used: 1,
    notes: '',
    service_date: new Date().toISOString().split('T')[0]
  });
  
  const [memberSearch, setMemberSearch] = useState('');

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([
      loadPackages(),
      loadStats(),
      loadUsageStats(),
      loadAssignedPackages(),
      loadMembers(),
      loadPlans(),
      loadDiscountSettings()
    ]);
    setLoading(false);
  };

  const loadPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data);
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-packages/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadUsageStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-packages/usage-stats/clinic`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsageStats(response.data);
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data || []);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadDiscountSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-packages/member-discount-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiscountSettings(response.data);
    } catch (error) {
      console.error('Error loading discount settings:', error);
    }
  };

  const saveDiscountSettings = async () => {
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/treatment-packages/member-discount-settings`, discountSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member discount settings saved!');
      setShowDiscountSettingsModal(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  // Calculate price preview when member is selected
  const calculatePricePreview = async (memberId) => {
    if (!memberId || !selectedPackageForAssign) return;
    
    setPriceLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/treatment-packages/calculate-price?package_id=${selectedPackageForAssign.id}&member_id=${memberId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPricePreview(response.data);
    } catch (error) {
      console.error('Error calculating price:', error);
      setPricePreview(null);
    } finally {
      setPriceLoading(false);
    }
  };

  const loadAssignedPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/treatment-packages/assigned/all/clinic`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignedPackages(response.data);
    } catch (error) {
      console.error('Error loading assigned packages:', error);
    }
  };

  const loadMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  // Calculate savings
  const calculateSavings = () => {
    const packagePrice = parseFloat(formData.price) || 0;
    const originalPrice = parseFloat(formData.original_price) || 0;
    
    if (originalPrice > packagePrice && originalPrice > 0) {
      const savings = originalPrice - packagePrice;
      const percentage = ((savings / originalPrice) * 100).toFixed(1);
      return { savings, percentage };
    }
    return null;
  };

  // Calculate original price from items
  const calculateOriginalPrice = () => {
    let total = 0;
    formData.items.forEach(item => {
      const unitPrice = parseFloat(item.unit_price) || 0;
      const qty = parseInt(item.quantity) || 1;
      total += unitPrice * qty;
    });
    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const data = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        discount_percentage: formData.discount_percentage ? parseFloat(formData.discount_percentage) : null,
        duration_days: formData.duration_days ? parseInt(formData.duration_days) : null,
        items: formData.items.filter(item => item.service_name.trim()).map(item => ({
          service_name: item.service_name,
          quantity: parseInt(item.quantity) || 1,
          description: item.description || `$${item.unit_price || 0} value each`
        })),
        is_active: formData.is_active
      };

      if (editingPackage) {
        await axios.patch(
          `${API_URL}/api/treatment-packages/${editingPackage.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Package updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/treatment-packages`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Package created successfully!');
      }

      setShowCreateModal(false);
      setEditingPackage(null);
      resetForm();
      loadPackages();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      original_price: '',
      discount_percentage: '',
      duration_days: '',
      items: [{ service_name: '', quantity: 1, unit_price: '', description: '' }],
      is_active: true
    });
  };

  const editPackage = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      description: pkg.description,
      price: pkg.price.toString(),
      original_price: '',
      discount_percentage: pkg.discount_percentage?.toString() || '',
      duration_days: pkg.duration_days?.toString() || '',
      items: pkg.items.length > 0 ? pkg.items.map(item => ({
        service_name: item.service_name,
        quantity: item.quantity,
        unit_price: '',
        description: item.description || ''
      })) : [{ service_name: '', quantity: 1, unit_price: '', description: '' }],
      is_active: pkg.is_active
    });
    setShowCreateModal(true);
  };

  const deletePackage = async (packageId) => {
    if (!window.confirm('Delete this package? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/treatment-packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Package deleted');
      loadPackages();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { service_name: '', quantity: 1, unit_price: '', description: '' }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  // Assign package to member
  const openAssignModal = (pkg) => {
    setSelectedPackageForAssign(pkg);
    setAssignForm({
      member_id: '',
      payment_method: 'cash',
      payment_notes: '',
      reference_number: '',
      start_date: new Date().toISOString().split('T')[0]
    });
    setPricePreview(null);
    setMemberSearch('');
    setShowAssignModal(true);
  };

  const handleAssignPackage = async () => {
    if (!assignForm.member_id) {
      toast.error('Please select a member');
      return;
    }
    
    setAssignLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/treatment-packages/assign`, {
        member_id: assignForm.member_id,
        package_id: selectedPackageForAssign.id,
        payment_method: assignForm.payment_method,
        payment_notes: assignForm.payment_notes || null,
        reference_number: assignForm.reference_number || null,
        start_date: assignForm.start_date || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const discountApplied = response.data.discount_info?.total_discount_amount > 0;
      toast.success(`Package assigned successfully!${discountApplied ? ' Discount applied!' : ''}`);
      setShowAssignModal(false);
      setPricePreview(null);
      loadAssignedPackages();
      loadUsageStats();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign package');
    } finally {
      setAssignLoading(false);
    }
  };

  // Handle member selection for price preview
  const handleMemberSelect = (member) => {
    setAssignForm({ ...assignForm, member_id: member.id });
    setMemberSearch(member.full_name);
    calculatePricePreview(member.id);
  };

  // View package detail
  const viewPackageDetail = (memberPkg) => {
    setSelectedMemberPackage(memberPkg);
    setShowDetailModal(true);
  };

  // Open usage modal
  const openUsageModal = (memberPkg) => {
    setSelectedMemberPackage(memberPkg);
    setUsageForm({
      item_index: 0,
      quantity_used: 1,
      notes: '',
      service_date: new Date().toISOString().split('T')[0]
    });
    setShowUsageModal(true);
  };

  const handleRecordUsage = async () => {
    setUsageLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/treatment-packages/assigned/${selectedMemberPackage.id}/use`,
        {
          item_index: parseInt(usageForm.item_index),
          quantity_used: parseInt(usageForm.quantity_used),
          notes: usageForm.notes || null,
          service_date: usageForm.service_date || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Recorded ${response.data.quantity_used}x ${response.data.item_name}. ${response.data.quantity_remaining} remaining.`);
      
      if (response.data.package_completed) {
        toast.success('🎉 Package completed!', { duration: 5000 });
      }
      
      setShowUsageModal(false);
      loadAssignedPackages();
      loadUsageStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record usage');
    } finally {
      setUsageLoading(false);
    }
  };

  // Filter members for search
  const filteredMembers = members.filter(m => 
    m.full_name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.email?.toLowerCase().includes(memberSearch.toLowerCase())
  );

  // Get status badge color
  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-100 text-green-700 border-0"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'completed': return <Badge className="bg-blue-100 text-blue-700 border-0"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'expired': return <Badge className="bg-orange-100 text-orange-700 border-0"><AlertCircle className="h-3 w-3 mr-1" />Expired</Badge>;
      case 'cancelled': return <Badge className="bg-red-100 text-red-700 border-0"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Add/update tier discount
  const updateTierDiscount = (planId, planName, discountPercent) => {
    const existing = discountSettings.tier_discounts.find(t => t.plan_id === planId);
    if (existing) {
      setDiscountSettings({
        ...discountSettings,
        tier_discounts: discountSettings.tier_discounts.map(t => 
          t.plan_id === planId ? { ...t, discount_percent: discountPercent } : t
        )
      });
    } else {
      setDiscountSettings({
        ...discountSettings,
        tier_discounts: [...discountSettings.tier_discounts, { plan_id: planId, plan_name: planName, discount_percent: discountPercent }]
      });
    }
  };

  const getTierDiscount = (planId) => {
    const tier = discountSettings.tier_discounts.find(t => t.plan_id === planId);
    return tier?.discount_percent ?? '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Treatment Packages</h2>
          <p className="text-gray-600 dark:text-gray-400">Create and manage bundled treatment packages</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setShowDiscountSettingsModal(true)} variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Member Discounts
            {discountSettings.enabled && (
              <Badge className="ml-2 bg-green-100 text-green-700 border-0 text-xs">ON</Badge>
            )}
          </Button>
          <Button onClick={loadAll} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setEditingPackage(null);
              setShowCreateModal(true);
            }}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Package
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Package className="h-8 w-8 mx-auto mb-2 text-teal-600" />
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.total_packages || 0}</p>
              <p className="text-xs text-gray-500">Total Packages</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold text-green-600">{stats?.active_packages || 0}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <p className="text-2xl font-bold text-blue-600">{usageStats?.active_packages || 0}</p>
              <p className="text-xs text-gray-500">In Use</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <TrendingUp className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <p className="text-2xl font-bold text-purple-600">{stats?.total_times_purchased || 0}</p>
              <p className="text-xs text-gray-500">Times Sold</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
              <p className="text-2xl font-bold text-emerald-600">{usageStats?.completed_packages || 0}</p>
              <p className="text-xs text-gray-500">Completed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <DollarSign className="h-8 w-8 mx-auto mb-2 text-teal-600" />
              <p className="text-2xl font-bold text-teal-600">${(usageStats?.total_revenue || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Revenue</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="packages" className="gap-2">
            <Package className="h-4 w-4" />
            Packages ({packages.length})
          </TabsTrigger>
          <TabsTrigger value="assigned" className="gap-2">
            <Users className="h-4 w-4" />
            Assigned ({assignedPackages.length})
          </TabsTrigger>
        </TabsList>

        {/* Packages Tab */}
        <TabsContent value="packages" className="mt-6">
          {packages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">No treatment packages yet</p>
                <p className="text-sm mt-1">Create your first package to start bundling treatments</p>
                <Button onClick={() => setShowCreateModal(true)} className="mt-4 bg-teal-600 hover:bg-teal-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Package
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <Card key={pkg.id} className={`transition-all hover:shadow-lg ${pkg.is_active ? '' : 'opacity-60'}`}>
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl">{pkg.name}</CardTitle>
                        {pkg.discount_percentage > 0 && (
                          <Badge className="mt-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white border-0">
                            <Percent className="h-3 w-3 mr-1" />
                            Save {pkg.discount_percentage}%
                          </Badge>
                        )}
                      </div>
                      {pkg.is_active ? (
                        <Badge className="bg-green-100 text-green-700 border-0">Active</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700 border-0">Inactive</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold text-teal-600">${pkg.price.toFixed(2)}</span>
                      {pkg.discount_percentage > 0 && (
                        <span className="text-lg text-gray-400 line-through">
                          ${(pkg.price / (1 - pkg.discount_percentage / 100)).toFixed(2)}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{pkg.description}</p>

                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Includes:</p>
                      <ul className="space-y-1">
                        {pkg.items.slice(0, 3).map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{item.quantity}x {item.service_name}</span>
                          </li>
                        ))}
                        {pkg.items.length > 3 && (
                          <li className="text-sm text-teal-600">+{pkg.items.length - 3} more treatments</li>
                        )}
                      </ul>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      {pkg.duration_days && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {pkg.duration_days} days
                        </span>
                      )}
                      {pkg.times_purchased > 0 && (
                        <span className="flex items-center gap-1">
                          <TrendingUp className="h-4 w-4" />
                          {pkg.times_purchased} sold
                        </span>
                      )}
                    </div>

                    <Separator />

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => openAssignModal(pkg)}
                        disabled={!pkg.is_active}
                        className="flex-1 bg-teal-600 hover:bg-teal-700"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Assign
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => editPackage(pkg)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deletePackage(pkg.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Assigned Packages Tab */}
        <TabsContent value="assigned" className="mt-6">
          {assignedPackages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-lg font-medium">No packages assigned yet</p>
                <p className="text-sm mt-1">Assign packages to members to track their usage</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {assignedPackages.map((mp) => {
                const totalItems = mp.items.reduce((sum, item) => sum + item.quantity_total, 0);
                const usedItems = mp.items.reduce((sum, item) => sum + item.quantity_used, 0);
                const progress = totalItems > 0 ? (usedItems / totalItems) * 100 : 0;
                
                return (
                  <Card key={mp.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        {/* Member Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-teal-700 font-semibold">
                                {mp.member_name?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 dark:text-white truncate">
                                {mp.member_name}
                              </p>
                              <p className="text-sm text-gray-500 truncate">{mp.member_email}</p>
                            </div>
                          </div>
                        </div>

                        {/* Package Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white">{mp.package_name}</p>
                          <p className="text-sm text-teal-600">${mp.package_price?.toFixed(2)}</p>
                        </div>

                        {/* Progress */}
                        <div className="w-40 hidden md:block">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>{usedItems}/{totalItems} used</span>
                            <span>{progress.toFixed(0)}%</span>
                          </div>
                          <Progress value={progress} className="h-2" />
                        </div>

                        {/* Expiry */}
                        <div className="w-28 text-center hidden lg:block">
                          {mp.expiry_date ? (
                            <div>
                              <p className="text-xs text-gray-500">Expires</p>
                              <p className="text-sm font-medium">
                                {new Date(mp.expiry_date).toLocaleDateString()}
                              </p>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">No expiry</p>
                          )}
                        </div>

                        {/* Status */}
                        <div className="w-28">
                          {getStatusBadge(mp.status)}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          {mp.status === 'active' && (
                            <Button
                              size="sm"
                              onClick={() => openUsageModal(mp)}
                              className="bg-teal-600 hover:bg-teal-700"
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Use
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewPackageDetail(mp)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Package Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-teal-600" />
              {editingPackage ? 'Edit Package' : 'Create Treatment Package'}
            </DialogTitle>
            <DialogDescription>Bundle multiple treatments into an attractive package for your patients</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Package Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Complete Facial Rejuvenation"
                />
              </div>
              <div className="space-y-2">
                <Label>Package Price *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    required
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="1499.00"
                    className="pl-10"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the benefits and what's included in this package..."
                rows={2}
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Original Value (for savings display)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="Auto-calculate or enter"
                    className="pl-10"
                  />
                </div>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setFormData({ ...formData, original_price: calculateOriginalPrice().toString() })}
                  className="text-xs"
                >
                  Calculate from items
                </Button>
              </div>
              <div className="space-y-2">
                <Label>Discount %</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                  placeholder="15"
                />
              </div>
              <div className="space-y-2">
                <Label>Validity (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: e.target.value })}
                  placeholder="90, 180, 365"
                />
              </div>
            </div>

            {/* Savings Preview */}
            {calculateSavings() && (
              <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Gift className="h-5 w-5" />
                  <span className="font-semibold">
                    Patients save ${calculateSavings().savings.toFixed(2)} ({calculateSavings().percentage}% off)
                  </span>
                </div>
              </div>
            )}

            {/* Package Items */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                Package Items *
                <Badge variant="outline" className="font-normal">{formData.items.length} items</Badge>
              </Label>
              {formData.items.map((item, index) => (
                <div key={index} className="p-3 border rounded-lg space-y-2 bg-gray-50 dark:bg-slate-800">
                  <div className="flex gap-2">
                    <Input
                      required
                      value={item.service_name}
                      onChange={(e) => updateItem(index, 'service_name', e.target.value)}
                      placeholder="Treatment/Service name"
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      placeholder="Qty"
                      className="w-20"
                    />
                    <div className="relative w-28">
                      <DollarSign className="absolute left-2 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        placeholder="Unit $"
                        className="pl-7"
                      />
                    </div>
                    {formData.items.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(index)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addItem} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Treatment
              </Button>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm">Package is active and available for purchase</span>
            </label>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Package Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-teal-600" />
              Assign Package to Member
            </DialogTitle>
            <DialogDescription>
              {selectedPackageForAssign?.name} - ${selectedPackageForAssign?.price?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Member Search & Select */}
            <div className="space-y-2">
              <Label>Select Member *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  className="pl-10"
                />
              </div>
              {memberSearch && !assignForm.member_id && (
                <div className="max-h-40 overflow-y-auto border rounded-lg">
                  {filteredMembers.slice(0, 10).map(member => (
                    <div
                      key={member.id}
                      onClick={() => handleMemberSelect(member)}
                      className={`p-2 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer ${
                        assignForm.member_id === member.id ? 'bg-teal-50 dark:bg-teal-900/30' : ''
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{member.full_name}</p>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                        {member.membership_status === 'active' && (
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                            <Award className="h-3 w-3 mr-1" />
                            Member
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {assignForm.member_id && memberSearch && (
                <Button variant="ghost" size="sm" onClick={() => {
                  setAssignForm({ ...assignForm, member_id: '' });
                  setMemberSearch('');
                  setPricePreview(null);
                }} className="text-xs text-gray-500">
                  Change member
                </Button>
              )}
            </div>

            {/* Member Discount Preview */}
            {priceLoading && (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Calculating discounts...
              </div>
            )}
            
            {pricePreview && pricePreview.has_active_membership && pricePreview.member_discount_percent > 0 && (
              <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                  <Sparkles className="h-5 w-5" />
                  <span className="font-semibold">Member Discount Applied!</span>
                </div>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {pricePreview.membership_plan_name} member gets {pricePreview.member_discount_percent}% off
                  {pricePreview.discount_stacked && pricePreview.package_discount_percent > 0 && 
                    ` (stacked with ${pricePreview.package_discount_percent}% package discount)`
                  }
                </p>
              </div>
            )}

            {/* Start Date */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={assignForm.start_date}
                onChange={(e) => setAssignForm({ ...assignForm, start_date: e.target.value })}
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select value={assignForm.payment_method} onValueChange={(v) => setAssignForm({ ...assignForm, payment_method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reference Number */}
            {['credit_card', 'debit_card', 'bank_transfer', 'check'].includes(assignForm.payment_method) && (
              <div className="space-y-2">
                <Label>Reference Number</Label>
                <Input
                  value={assignForm.reference_number}
                  onChange={(e) => setAssignForm({ ...assignForm, reference_number: e.target.value })}
                  placeholder="Transaction ID or check number"
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label>Payment Notes</Label>
              <Textarea
                value={assignForm.payment_notes}
                onChange={(e) => setAssignForm({ ...assignForm, payment_notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>

            {/* Summary */}
            <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Package:</span>
                <span className="font-medium">{selectedPackageForAssign?.name}</span>
              </div>
              <div className="flex justify-between">
                <span>Validity:</span>
                <span>{selectedPackageForAssign?.duration_days ? `${selectedPackageForAssign.duration_days} days` : 'No expiry'}</span>
              </div>
              
              {pricePreview ? (
                <>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-sm">
                    <span>Original Price:</span>
                    <span className={pricePreview.total_discount_amount > 0 ? 'line-through text-gray-400' : ''}>
                      ${pricePreview.original_price?.toFixed(2)}
                    </span>
                  </div>
                  {pricePreview.package_discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Package Discount ({pricePreview.package_discount_percent}%):</span>
                      <span>-${pricePreview.package_discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  {pricePreview.member_discount_amount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Member Discount ({pricePreview.member_discount_percent}%):</span>
                      <span>-${pricePreview.member_discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  {pricePreview.total_discount_amount > 0 && (
                    <div className="flex justify-between text-sm font-medium text-green-600">
                      <span>Total Savings:</span>
                      <span>${pricePreview.total_discount_amount?.toFixed(2)}</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Final Price:</span>
                    <span className="text-teal-600">${pricePreview.final_price?.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <>
                  <Separator className="my-2" />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total:</span>
                    <span className="text-teal-600">${selectedPackageForAssign?.price?.toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button 
              onClick={handleAssignPackage} 
              disabled={!assignForm.member_id || assignLoading}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {assignLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Assign & Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Usage Modal */}
      <Dialog open={showUsageModal} onOpenChange={setShowUsageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-teal-600" />
              Record Treatment Usage
            </DialogTitle>
            <DialogDescription>
              {selectedMemberPackage?.member_name} - {selectedMemberPackage?.package_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Select Treatment */}
            <div className="space-y-2">
              <Label>Select Treatment *</Label>
              <Select 
                value={usageForm.item_index.toString()} 
                onValueChange={(v) => setUsageForm({ ...usageForm, item_index: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {selectedMemberPackage?.items?.map((item, idx) => (
                    <SelectItem 
                      key={idx} 
                      value={idx.toString()}
                      disabled={item.quantity_remaining === 0}
                    >
                      {item.service_name} ({item.quantity_remaining}/{item.quantity_total} remaining)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Quantity */}
            <div className="space-y-2">
              <Label>Quantity Used</Label>
              <Input
                type="number"
                min="1"
                max={selectedMemberPackage?.items?.[usageForm.item_index]?.quantity_remaining || 1}
                value={usageForm.quantity_used}
                onChange={(e) => setUsageForm({ ...usageForm, quantity_used: parseInt(e.target.value) || 1 })}
              />
            </div>

            {/* Service Date */}
            <div className="space-y-2">
              <Label>Service Date</Label>
              <Input
                type="date"
                value={usageForm.service_date}
                onChange={(e) => setUsageForm({ ...usageForm, service_date: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={usageForm.notes}
                onChange={(e) => setUsageForm({ ...usageForm, notes: e.target.value })}
                placeholder="Optional notes about this session..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowUsageModal(false)}>Cancel</Button>
            <Button 
              onClick={handleRecordUsage} 
              disabled={usageLoading}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {usageLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Record Usage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Package Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-teal-600" />
              Package Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedMemberPackage && (
            <div className="space-y-4">
              {/* Member Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                  <span className="text-teal-700 font-bold text-lg">
                    {selectedMemberPackage.member_name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-semibold">{selectedMemberPackage.member_name}</p>
                  <p className="text-sm text-gray-500">{selectedMemberPackage.member_email}</p>
                </div>
              </div>

              {/* Package Info */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Package:</span>
                  <span className="font-medium">{selectedMemberPackage.package_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Price:</span>
                  <span className="font-medium text-teal-600">${selectedMemberPackage.package_price?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  {getStatusBadge(selectedMemberPackage.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Assigned:</span>
                  <span>{new Date(selectedMemberPackage.assigned_at).toLocaleDateString()}</span>
                </div>
                {selectedMemberPackage.expiry_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expires:</span>
                    <span>{new Date(selectedMemberPackage.expiry_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              <Separator />

              {/* Usage Progress */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <History className="h-4 w-4" />
                  Treatment Usage
                </Label>
                {selectedMemberPackage.items?.map((item, idx) => (
                  <div key={idx} className="p-3 border rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">{item.service_name}</span>
                      <span className="text-sm text-gray-500">
                        {item.quantity_used}/{item.quantity_total} used
                      </span>
                    </div>
                    <Progress 
                      value={(item.quantity_used / item.quantity_total) * 100} 
                      className="h-2"
                    />
                    {item.quantity_remaining === 0 && (
                      <Badge className="mt-2 bg-green-100 text-green-700 border-0">Completed</Badge>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TreatmentPackages;
